import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { USER_LEVEL, USER_ROLE, type CreatePinMessageInput, type PinMessage } from '@fdh/shared';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { AppButton } from '../../components/ui/AppButton';
import { useToast } from '../../components/ui/ToastProvider';
import { pinMessageApi } from '../../lib/api';
import { isLimitReachedError, emitPinMessageActiveLimitIfReached } from '../../lib/limitError';
import { usePinMessageCreationGate } from '../../hooks/usePinMessageCreationGate';
import {
  PIN_MESSAGE_ICON_OPTIONS,
  pinMessageIconLabelKey,
  pinMessageIconSvg,
  pinMessageIconTheme,
} from '../../components/pinMessage/pinMessageIconTheme';
import { IsoDatePicker } from '../../components/IsoDatePicker';

type PinMessageDraft = CreatePinMessageInput;

const defaultDraft: PinMessageDraft = {
  title: '',
  content: '',
  visibility: 'both',
  iconType: 'info',
  isActive: true,
  displayFromDate: '',
  displayToDate: '',
};

export const PinMessageFormPage = () => {
  const { user } = useAuth();
  const { t } = useLanguage();
  const { showErrorToast, showSuccessToast } = useToast();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEdit = Boolean(id);
  const { guardCreatePinMessageOnMount } = usePinMessageCreationGate();

  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [iconMenuOpen, setIconMenuOpen] = useState(false);
  const [limits, setLimits] = useState({
    maxItems: 20,
    titleMaxLength: 30,
    contentMaxLength: 300,
  });
  const [draft, setDraft] = useState<PinMessageDraft>(defaultDraft);
  const [initialIsActive, setInitialIsActive] = useState(true);
  const [siblingItems, setSiblingItems] = useState<PinMessage[]>([]);
  const iconMenuRef = useRef<HTMLDivElement | null>(null);

  const titleLength = useMemo(() => draft.title.trim().length, [draft.title]);
  const contentLength = useMemo(() => draft.content.trim().length, [draft.content]);

  const load = useCallback(async () => {
    setError('');
    try {
      const [limitRes, listRes] = await Promise.all([
        pinMessageApi.getLimits(),
        isEdit ? pinMessageApi.list(true) : Promise.resolve(null),
      ]);
      if (limitRes.success && limitRes.data) {
        setLimits(limitRes.data);
      }
      if (isEdit) {
        const items = listRes?.success && listRes.data ? listRes.data.items : [];
        setSiblingItems(items);
        const target = items.find((item) => item.id === id) ?? null;
        if (!target) {
          setError(t('pinMessage.error.notFound'));
          return;
        }
        setInitialIsActive(target.isActive);
        setDraft({
          title: target.title,
          content: target.content,
          visibility: target.visibility,
          iconType: target.iconType,
          isActive: target.isActive,
          displayFromDate: target.displayFromDate,
          displayToDate: target.displayToDate,
        });
      }
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError.message : t('pinMessage.error.loadForm'));
    } finally {
      setLoading(false);
    }
  }, [id, isEdit, t]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (isEdit) {
      return;
    }
    const guard = async () => {
      const allowed = await guardCreatePinMessageOnMount();
      if (!allowed) {
        navigate('/pin-messages', { replace: true });
      }
    };
    void guard();
  }, [guardCreatePinMessageOnMount, isEdit, navigate]);

  useEffect(() => {
    if (!iconMenuOpen) {
      return;
    }
    const handleClickOutside = (event: MouseEvent) => {
      if (!iconMenuRef.current) {
        return;
      }
      if (iconMenuRef.current.contains(event.target as Node)) {
        return;
      }
      setIconMenuOpen(false);
    };
    window.addEventListener('mousedown', handleClickOutside);
    return () => window.removeEventListener('mousedown', handleClickOutside);
  }, [iconMenuOpen]);

  const handleBack = () => {
    navigate(-1);
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError('');
    if (titleLength === 0 || contentLength === 0) {
      setError(t('pinMessage.error.titleContentRequired'));
      return;
    }
    if (titleLength > limits.titleMaxLength) {
      setError(t('pinMessage.error.titleMax', { max: limits.titleMaxLength }));
      return;
    }
    if (contentLength > limits.contentMaxLength) {
      setError(t('pinMessage.error.contentMax', { max: limits.contentMaxLength }));
      return;
    }
    if (draft.displayFromDate && draft.displayToDate && draft.displayFromDate > draft.displayToDate) {
      setError(t('pinMessage.error.dateOrder'));
      return;
    }
    if (
      isEdit &&
      draft.isActive &&
      !initialIsActive &&
      user &&
      emitPinMessageActiveLimitIfReached(
        siblingItems.filter((row) => row.isActive).length,
        limits.maxItems,
        siblingItems.length,
        user.level ?? USER_LEVEL.TRIAL,
      )
    ) {
      return;
    }
    setSaving(true);
    try {
      if (isEdit && id) {
        await pinMessageApi.update(id, draft);
        showSuccessToast(t('toast.pinMessage.updateSuccess'));
      } else {
        await pinMessageApi.create(draft);
        showSuccessToast(t('toast.pinMessage.createSuccess'));
      }
      navigate('/pin-messages', { replace: true });
    } catch (submitError) {
      if (isLimitReachedError(submitError)) {
        return;
      }
      const message = submitError instanceof Error ? submitError.message : t('pinMessage.error.save');
      setError(message);
      showErrorToast(message);
    } finally {
      setSaving(false);
    }
  };

  if (!user) return null;
  if (user.role !== USER_ROLE.EMPLOYER) {
    return <Navigate to="/home" replace />;
  }

  const getIconLabel = (iconType: PinMessage['iconType']): string => t(pinMessageIconLabelKey(iconType));
  const renderClearDateButton = (onClear: () => void, ariaLabel: string) => (
    <button
      type="button"
      onClick={onClear}
      className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-gray-500 hover:bg-gray-100 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:text-gray-300 dark:hover:bg-gray-700 dark:hover:text-gray-100"
      aria-label={ariaLabel}
    >
      <svg className="h-4 w-4" viewBox="0 0 20 20" fill="none" stroke="currentColor" aria-hidden>
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 6l8 8M14 6l-8 8" />
      </svg>
    </button>
  );

  return (
    <main className="min-h-full bg-gray-50 dark:bg-gray-950">
      <header className="z-10 bg-white shadow-sm dark:bg-gray-900 dark:shadow-black/20">
        <div className="px-4 py-4">
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={handleBack}
              className="flex h-10 w-10 items-center justify-center rounded-full text-gray-600 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:text-gray-200 dark:hover:bg-gray-800"
              aria-label={t('task.action.back')}
            >
              <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <h1 className="text-xl font-bold text-gray-900 dark:text-gray-100">
              {isEdit ? t('pinMessage.form.editTitle') : t('pinMessage.form.addTitle')}
            </h1>
          </div>
        </div>
      </header>

      <div className="px-4 py-5 pb-16">
      {loading ? <p className="text-sm text-gray-500">{t('common.loading')}</p> : null}
      {error ? <p className="mb-3 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">{error}</p> : null}

      {!loading ? (
        <form className="space-y-4" onSubmit={handleSubmit}>
          <div>
            <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200" htmlFor="pm-title">
              {t('pinMessage.field.title')}
            </label>
            <input
              id="pm-title"
              type="text"
              value={draft.title}
              maxLength={limits.titleMaxLength}
              onChange={(event) => setDraft((prev) => ({ ...prev, title: event.target.value }))}
              className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100"
              placeholder={t('pinMessage.field.titlePlaceholder')}
              required
            />
            <p className="mt-1 text-xs text-gray-500">{titleLength}/{limits.titleMaxLength}</p>
          </div>

          <div>
            <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200" htmlFor="pm-content">
              {t('pinMessage.field.content')}
            </label>
            <textarea
              id="pm-content"
              value={draft.content}
              maxLength={limits.contentMaxLength}
              onChange={(event) => setDraft((prev) => ({ ...prev, content: event.target.value }))}
              className="min-h-32 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100"
              placeholder={t('pinMessage.field.contentPlaceholder')}
              required
            />
            <p className="mt-1 text-xs text-gray-500">{contentLength}/{limits.contentMaxLength}</p>
          </div>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <label className="text-sm font-semibold text-gray-700 dark:text-gray-200">
              {t('pinMessage.field.visibility')}
              <select
                value={draft.visibility}
                onChange={(event) => setDraft((prev) => ({ ...prev, visibility: event.target.value as PinMessage['visibility'] }))}
                className="mt-1 block w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100"
              >
                <option value="both">{t('pinMessage.visibility.both')}</option>
                <option value="employer_only">{t('pinMessage.visibility.employerOnly')}</option>
              </select>
            </label>
            <label className="text-sm font-semibold text-gray-700 dark:text-gray-200">
              {t('pinMessage.field.icon')}
              <div ref={iconMenuRef} className="relative mt-1">
                <button
                  type="button"
                  onClick={() => setIconMenuOpen((prev) => !prev)}
                  className="flex w-full items-center justify-between rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100"
                  aria-haspopup="listbox"
                  aria-expanded={iconMenuOpen}
                >
                  <span className="inline-flex items-center gap-2">
                    <span
                      className={`inline-flex h-6 w-6 items-center justify-center rounded-full ${pinMessageIconTheme(draft.iconType).badge}`}
                    >
                      {pinMessageIconSvg(draft.iconType)}
                    </span>
                    <span>{getIconLabel(draft.iconType)}</span>
                  </span>
                  <svg className="h-4 w-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                {iconMenuOpen ? (
                  <ul
                    role="listbox"
                    className="absolute z-20 mt-1 w-full rounded-lg border border-gray-200 bg-white p-1 shadow-lg dark:border-gray-600 dark:bg-gray-900"
                  >
                    {PIN_MESSAGE_ICON_OPTIONS.map((iconType) => (
                      <li key={iconType}>
                        <button
                          type="button"
                          onClick={() => {
                            setDraft((prev) => ({ ...prev, iconType }));
                            setIconMenuOpen(false);
                          }}
                          className="flex w-full items-center gap-2 rounded-md px-2 py-2 text-left text-sm text-gray-900 hover:bg-gray-100 dark:text-gray-100 dark:hover:bg-gray-800"
                        >
                          <span
                            className={`inline-flex h-6 w-6 items-center justify-center rounded-full ${pinMessageIconTheme(iconType).badge}`}
                          >
                            {pinMessageIconSvg(iconType)}
                          </span>
                          <span>{getIconLabel(iconType)}</span>
                        </button>
                      </li>
                    ))}
                  </ul>
                ) : null}
              </div>
            </label>
          </div>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div>
              <label
                className="block text-sm font-semibold text-gray-700 dark:text-gray-200"
                htmlFor="pm-display-from"
              >
                {t('pinMessage.field.displayFrom')}
              </label>
              <div className="mt-1 flex min-w-0 items-center gap-2">
                <IsoDatePicker
                  id="pm-display-from"
                  ariaLabel={t('pinMessage.field.displayFrom')}
                  value={draft.displayFromDate}
                  onChange={(next) => setDraft((prev) => ({ ...prev, displayFromDate: next }))}
                  maxDate={draft.displayToDate || undefined}
                  valueFormat="iso"
                  className="min-w-0 flex-1"
                />
                {draft.displayFromDate !== ''
                  ? renderClearDateButton(
                    () => setDraft((prev) => ({ ...prev, displayFromDate: '' })),
                    t('pinMessage.field.displayFrom'),
                  )
                  : null}
              </div>
            </div>
            <div>
              <label
                className="block text-sm font-semibold text-gray-700 dark:text-gray-200"
                htmlFor="pm-display-to"
              >
                {t('pinMessage.field.displayTo')}
              </label>
              <div className="mt-1 flex min-w-0 items-center gap-2">
                <IsoDatePicker
                  id="pm-display-to"
                  ariaLabel={t('pinMessage.field.displayTo')}
                  value={draft.displayToDate}
                  onChange={(next) => setDraft((prev) => ({ ...prev, displayToDate: next }))}
                  minDate={draft.displayFromDate || undefined}
                  valueFormat="iso"
                  className="min-w-0 flex-1"
                />
                {draft.displayToDate !== ''
                  ? renderClearDateButton(
                    () => setDraft((prev) => ({ ...prev, displayToDate: '' })),
                    t('pinMessage.field.displayTo'),
                  )
                  : null}
              </div>
            </div>
          </div>

          <label className="flex items-center gap-2 text-sm font-semibold text-gray-700 dark:text-gray-200">
            <input
              type="checkbox"
              checked={draft.isActive}
              onChange={(event) => setDraft((prev) => ({ ...prev, isActive: event.target.checked }))}
            />
            {t('pinMessage.status.active')}
          </label>

          <div className="flex flex-wrap gap-2 pt-2">
            <AppButton type="submit" variant="primary" disabled={saving} className="flex-1 shadow-sm">
              {saving ? t('pinMessage.form.saving') : t('pinMessage.action.save')}
            </AppButton>
            <AppButton type="button" variant="secondary" onClick={() => navigate('/pin-messages')}>
              {t('pinMessage.action.cancel')}
            </AppButton>
          </div>
        </form>
      ) : null}
      </div>
    </main>
  );
};
